// EmailCampaignsPro.jsx — Full email marketing suite for Nova Med Spa
// Real send through Resend, AI generation through Claude, full tracking.

import { useState, useEffect, useMemo } from "react";
import EmailWysiwyg from "./EmailWysiwyg";
import { supabase } from "./supabase";

// ── DESIGN TOKENS ──────────────────────────────────────────────────────────────
const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldL:"#d4a853", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  teal:"#0d9488", tealL:"rgba(13,148,136,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

// ── SHARED UI ──────────────────────────────────────────────────────────────────
const inp = { width:"100%",padding:"10px 13px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box" };

function Btn({ children, v="primary", onClick, sz="md", icon, full, disabled, style }) {
  const sizes = { sm:{padding:"6px 12px",fontSize:11}, md:{padding:"10px 18px",fontSize:13}, lg:{padding:"13px 24px",fontSize:14} };
  const vars = {
    primary: { background:`linear-gradient(135deg,${C.gold},${C.goldD})`, color:"#fff", border:"none", boxShadow:"0 2px 8px rgba(180,140,60,0.3)" },
    success: { background:`linear-gradient(135deg,${C.green},#1e6b3f)`, color:"#fff", border:"none", boxShadow:"0 2px 8px rgba(46,139,87,0.3)" },
    danger:  { background:C.redL, color:C.red, border:`1px solid ${C.red}30` },
    ghost:   { background:"transparent", color:C.muted, border:`1px solid ${C.border}` },
    sec:     { background:C.goldBg, color:C.gold, border:`1px solid ${C.borderGold}` },
    dark:    { background:C.text, color:"#fff", border:"none" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ display:"inline-flex",alignItems:"center",gap:6,borderRadius:8,fontWeight:600,cursor:disabled?"not-allowed":"pointer",fontFamily:F,transition:"all 0.15s",whiteSpace:"nowrap",justifyContent:"center",opacity:disabled?0.5:1,...sizes[sz],...(vars[v]||vars.ghost),...(full?{width:"100%"}:{}),...style }}>
      {icon && <span style={{ fontSize:14 }}>{icon}</span>}
      {children}
    </button>
  );
}

function Card({ children, style, onClick }) {
  return (
    <div onClick={onClick} style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)",...style }}>{children}</div>
  );
}

function Bdg({ children, color = C.muted, bg }) {
  return (
    <span style={{ display:"inline-flex",alignItems:"center",gap:4,fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:20,background:bg||(color+"15"),color,border:`1px solid ${color}25`,whiteSpace:"nowrap" }}>
      {children}
    </span>
  );
}

const STATUS_BADGE = {
  draft:     { label:"Draft", color:C.muted },
  scheduled: { label:"Scheduled", color:C.blue },
  sending:   { label:"Sending...", color:C.orange },
  sent:      { label:"Sent", color:C.green },
  failed:    { label:"Failed", color:C.red },
  cancelled: { label:"Cancelled", color:C.muted },
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────
// IMAGE UPLOADER (uploads to Supabase Storage and returns public URL)
// ────────────────────────────────────────────────────────────────────────────
function ImageUploader({ onUploaded, currentUrl }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("File too large (max 5MB)");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      const fileName = `email-${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("email-images")
        .upload(fileName, file, { contentType: file.type, cacheControl: "31536000" });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("email-images").getPublicUrl(fileName);
      onUploaded(data.publicUrl);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {currentUrl ? (
        <div style={{ position:"relative",display:"inline-block",marginBottom:8 }}>
          <img src={currentUrl} alt="upload" style={{ maxWidth:"100%",maxHeight:200,borderRadius:8,display:"block",border:`1px solid ${C.border}` }} />
          <button onClick={() => onUploaded("")} style={{ position:"absolute",top:6,right:6,width:24,height:24,background:"rgba(0,0,0,0.7)",color:"#fff",border:"none",borderRadius:"50%",cursor:"pointer",fontSize:12 }}>×</button>
        </div>
      ) : null}
      <label style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 14px",background:C.goldBg,color:C.gold,border:`1px dashed ${C.borderGold}`,borderRadius:8,cursor:uploading?"wait":"pointer",fontSize:12,fontWeight:600,fontFamily:F }}>
        <input type="file" accept="image/*" disabled={uploading} onChange={e => upload(e.target.files?.[0])} style={{ display:"none" }} />
        {uploading ? "⏳ Uploading..." : currentUrl ? "🔄 Replace image" : "📁 Choose image (JPG/PNG, max 5MB)"}
      </label>
      {error && <div style={{ marginTop:6,fontSize:11,color:C.red }}>❌ {error}</div>}
    </div>
  );
}

export default function EmailCampaignsPro() {
  const [view, setView] = useState("list"); // 'list' | 'compose' | 'analytics' | 'suppressions'
  const [campaigns, setCampaigns] = useState([]);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [analyticsCampaign, setAnalyticsCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCampaigns = async () => {
    const { data } = await supabase.from("email_campaigns").select("*").order("created_at", { ascending: false });
    setCampaigns(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadCampaigns();
    const ch = supabase.channel("email-campaigns-realtime")
      .on("postgres_changes", { event:"*", schema:"public", table:"email_campaigns" }, loadCampaigns)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // ── Compose new
  if (view === "compose") {
    return (
      <Composer
        existing={editingCampaign}
        onClose={() => { setView("list"); setEditingCampaign(null); }}
        onSent={() => { setView("list"); setEditingCampaign(null); loadCampaigns(); }}
      />
    );
  }

  // ── Analytics drill-down
  if (view === "analytics" && analyticsCampaign) {
    return (
      <AnalyticsDetail
        campaign={analyticsCampaign}
        onClose={() => { setView("list"); setAnalyticsCampaign(null); }}
      />
    );
  }

  // ── Suppressions list
  if (view === "suppressions") {
    return <Suppressions onClose={() => setView("list")} />;
  }

  // ── Default list view
  return (
    <CampaignList
      campaigns={campaigns}
      loading={loading}
      onCompose={() => { setEditingCampaign(null); setView("compose"); }}
      onEdit={(c) => { setEditingCampaign(c); setView("compose"); }}
      onAnalytics={(c) => { setAnalyticsCampaign(c); setView("analytics"); }}
      onSuppressions={() => setView("suppressions")}
      reload={loadCampaigns}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CAMPAIGN LIST VIEW
// ────────────────────────────────────────────────────────────────────────────
function CampaignList({ campaigns, loading, onCompose, onEdit, onAnalytics, onSuppressions, reload }) {
  const totals = useMemo(() => campaigns.reduce((a, c) => ({
    sent: a.sent + (c.sent_count || 0),
    delivered: a.delivered + (c.delivered_count || 0),
    opened: a.opened + (c.opened_count || 0),
    clicked: a.clicked + (c.clicked_count || 0),
  }), { sent:0, delivered:0, opened:0, clicked:0 }), [campaigns]);

  const openRate = totals.delivered > 0 ? Math.round((totals.opened / totals.delivered) * 100) : 0;
  const clickRate = totals.delivered > 0 ? Math.round((totals.clicked / totals.delivered) * 100) : 0;

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign and all tracking data?")) return;
    await supabase.from("email_recipients").delete().eq("campaign_id", id);
    await supabase.from("email_events").delete().eq("campaign_id", id);
    await supabase.from("email_campaigns").delete().eq("id", id);
    reload();
  };

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:18 }}>
      {/* Header */}
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10 }}>
        <div>
          <div style={{ fontSize:22,fontWeight:800,color:C.text,fontFamily:S }}>Email Campaigns</div>
          <div style={{ fontSize:12,color:C.muted,marginTop:2 }}>Real sends · Real tracking · Real results</div>
        </div>
        <div style={{ display:"flex",gap:8 }}>
          <Btn v="ghost" sz="sm" onClick={onSuppressions}>🚫 Suppressions</Btn>
          <Btn v="primary" icon="✉️" onClick={onCompose}>New Campaign</Btn>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12 }}>
        <StatTile label="Total Sent" value={totals.sent.toLocaleString()} color={C.gold} icon="📨" />
        <StatTile label="Delivered" value={totals.delivered.toLocaleString()} color={C.green} icon="✓" />
        <StatTile label="Open Rate" value={`${openRate}%`} color={C.blue} icon="👁" sub={`${totals.opened.toLocaleString()} opens`} />
        <StatTile label="Click Rate" value={`${clickRate}%`} color={C.purple} icon="🖱" sub={`${totals.clicked.toLocaleString()} clicks`} />
      </div>

      {/* Campaigns list */}
      <Card>
        <div style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:C.goldBg,fontSize:14,fontWeight:700,color:C.gold,fontFamily:S }}>
          All Campaigns ({campaigns.length})
        </div>
        {loading ? (
          <div style={{ padding:40,textAlign:"center",color:C.muted,fontSize:13 }}>Loading…</div>
        ) : campaigns.length === 0 ? (
          <EmptyState onCompose={onCompose} />
        ) : campaigns.map(c => (
          <CampaignRow key={c.id} campaign={c} onEdit={() => onEdit(c)} onAnalytics={() => onAnalytics(c)} onDelete={() => deleteCampaign(c.id)} />
        ))}
      </Card>
    </div>
  );
}

function StatTile({ label, value, color, icon, sub }) {
  return (
    <Card style={{ padding:"16px 18px" }}>
      <div style={{ fontSize:18,marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:24,fontWeight:800,color,fontFamily:S,marginBottom:2 }}>{value}</div>
      <div style={{ fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em" }}>{label}</div>
      {sub && <div style={{ fontSize:10,color:C.dim,marginTop:2 }}>{sub}</div>}
    </Card>
  );
}

function EmptyState({ onCompose }) {
  return (
    <div style={{ padding:"60px 20px",textAlign:"center" }}>
      <div style={{ fontSize:48,marginBottom:12 }}>✉️</div>
      <div style={{ fontSize:18,fontWeight:700,color:C.text,fontFamily:S,marginBottom:8 }}>No campaigns yet</div>
      <div style={{ fontSize:13,color:C.muted,marginBottom:24,maxWidth:400,margin:"0 auto 24px" }}>
        Create your first email campaign with AI-generated content. We'll handle delivery, tracking, and compliance.
      </div>
      <Btn v="primary" icon="✨" onClick={onCompose}>Create First Campaign</Btn>
    </div>
  );
}

function CampaignRow({ campaign, onEdit, onAnalytics, onDelete }) {
  const status = STATUS_BADGE[campaign.status] || STATUS_BADGE.draft;
  const sent = campaign.sent_count || 0;
  const opens = campaign.opened_count || 0;
  const clicks = campaign.clicked_count || 0;
  const openRate = sent > 0 ? Math.round((opens / sent) * 100) : 0;
  const clickRate = sent > 0 ? Math.round((clicks / sent) * 100) : 0;

  return (
    <div style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap" }}>
      <div style={{ flex:1,minWidth:240 }}>
        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4 }}>
          <div style={{ fontSize:14,fontWeight:700,color:C.text }}>{campaign.name}</div>
          <Bdg color={status.color}>{status.label}</Bdg>
        </div>
        <div style={{ fontSize:12,color:C.muted }}>{campaign.subject}</div>
        <div style={{ fontSize:11,color:C.dim,marginTop:3 }}>
          {new Date(campaign.created_at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}
          {campaign.scheduled_for && campaign.status === "scheduled" && (
            <span> · Scheduled for {new Date(campaign.scheduled_for).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}</span>
          )}
        </div>
      </div>

      {sent > 0 && (
        <div style={{ display:"flex",gap:14,fontSize:11,color:C.muted }}>
          <Mini label="Sent" value={sent.toLocaleString()} />
          <Mini label="Open" value={`${openRate}%`} color={C.blue} />
          <Mini label="Click" value={`${clickRate}%`} color={C.purple} />
          <Mini label="Booked" value={(campaign.booked_count || 0).toLocaleString()} color={C.green || C.text} />
        </div>
      )}

      <div style={{ display:"flex",gap:6 }}>
        {campaign.status === "draft" && <Btn sz="sm" v="sec" onClick={onEdit}>Edit</Btn>}
        {sent > 0 && <Btn sz="sm" v="ghost" onClick={onAnalytics}>📊 Stats</Btn>}
        <Btn sz="sm" v="ghost" onClick={onDelete}>🗑</Btn>
      </div>
    </div>
  );
}

function Mini({ label, value, color = C.text }) {
  return (
    <div style={{ textAlign:"center" }}>
      <div style={{ fontSize:14,fontWeight:800,color,fontFamily:S }}>{value}</div>
      <div style={{ fontSize:9,color:C.dim,textTransform:"uppercase" }}>{label}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// COMPOSER (3 steps: Content → Audience → Send)
// ────────────────────────────────────────────────────────────────────────────
function Composer({ existing, onClose, onSent }) {
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(existing || {
    name: "",
    subject: "",
    preheader: "",
    html_body: "",
    from_name: "Nova Med Spa",
    from_email: "admin@novamedicalspa.com",
    audience_type: "all",
    audience_filter: {},
    scheduled_for: null,
  });
  const [campaignId, setCampaignId] = useState(existing?.id || null);
  const [saving, setSaving] = useState(false);

  // Save the draft to DB (creates or updates)
  const saveDraft = async () => {
    setSaving(true);
    const payload = {
      name: draft.name || "Untitled Campaign",
      subject: draft.subject || "",
      preheader: draft.preheader || "",
      html_body: draft.html_body || "",
      from_name: draft.from_name || "Nova Med Spa",
      from_email: draft.from_email || "admin@novamedicalspa.com",
      audience_type: draft.audience_type || "all",
      audience_filter: draft.audience_filter || {},
      scheduled_for: draft.scheduled_for,
      status: "draft",
      updated_at: new Date().toISOString(),
    };
    let id = campaignId;
    if (id) {
      await supabase.from("email_campaigns").update(payload).eq("id", id);
    } else {
      const { data } = await supabase.from("email_campaigns").insert([payload]).select().single();
      id = data?.id;
      setCampaignId(id);
    }
    setSaving(false);
    return id;
  };

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:18 }}>
      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
        <div style={{ display:"flex",alignItems:"center",gap:12 }}>
          <Btn v="ghost" sz="sm" onClick={onClose}>← Back</Btn>
          <div>
            <div style={{ fontSize:18,fontWeight:800,color:C.text,fontFamily:S }}>{existing ? "Edit Campaign" : "New Campaign"}</div>
            <div style={{ fontSize:11,color:C.muted }}>Step {step} of 3</div>
          </div>
        </div>
        <input
          type="text"
          placeholder="Campaign name (internal)"
          value={draft.name}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          style={{ ...inp, maxWidth:280 }}
        />
      </div>

      {/* Stepper */}
      <Stepper step={step} setStep={setStep} draft={draft} />

      {/* Step content */}
      {step === 1 && <StepContent draft={draft} setDraft={setDraft} />}
      {step === 2 && <StepAudience draft={draft} setDraft={setDraft} campaignId={campaignId} saveDraft={saveDraft} />}
      {step === 3 && <StepSend draft={draft} setDraft={setDraft} campaignId={campaignId} saveDraft={saveDraft} onSent={onSent} />}

      {/* Bottom nav */}
      <div style={{ display:"flex",gap:10,justifyContent:"space-between",borderTop:`1px solid ${C.border}`,paddingTop:14 }}>
        <div>
          {step > 1 && <Btn v="ghost" onClick={() => setStep(step - 1)}>← Previous</Btn>}
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <Btn v="ghost" onClick={async () => { await saveDraft(); onClose(); }} disabled={saving}>
            {saving ? "Saving..." : "💾 Save Draft"}
          </Btn>
          {step < 3 && (
            <Btn v="primary" disabled={!canProceed(step, draft)} onClick={async () => {
              await saveDraft();
              setStep(step + 1);
            }}>
              Continue →
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

function canProceed(step, draft) {
  if (step === 1) return draft.subject && draft.html_body;
  if (step === 2) return true;
  return true;
}

function Stepper({ step, setStep, draft }) {
  const steps = [
    { n:1, label:"Content", desc:"Subject, body, design" },
    { n:2, label:"Audience", desc:"Who gets this email" },
    { n:3, label:"Review & Send", desc:"Test, schedule, launch" },
  ];
  return (
    <div style={{ display:"flex",alignItems:"center",gap:6,padding:"14px 18px",background:C.card,border:`1px solid ${C.border}`,borderRadius:10 }}>
      {steps.map((s, i) => (
        <div key={s.n} style={{ display:"flex",alignItems:"center",flex:1 }}>
          <button onClick={() => setStep(s.n)} style={{ display:"flex",alignItems:"center",gap:10,background:"transparent",border:"none",cursor:"pointer",flex:1,textAlign:"left",padding:0 }}>
            <div style={{ width:32,height:32,borderRadius:"50%",background:step>=s.n?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,color:step>=s.n?"#fff":C.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,flexShrink:0 }}>
              {step > s.n ? "✓" : s.n}
            </div>
            <div>
              <div style={{ fontSize:13,fontWeight:700,color:step>=s.n?C.text:C.muted }}>{s.label}</div>
              <div style={{ fontSize:10,color:C.dim }}>{s.desc}</div>
            </div>
          </button>
          {i < steps.length - 1 && <div style={{ height:2,flex:1,background:step>s.n?C.gold:C.border,margin:"0 8px" }} />}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// STEP 1: CONTENT
// ────────────────────────────────────────────────────────────────────────────
function StepContent({ draft, setDraft }) {
  const [aiOpen, setAiOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("edit"); // 'desktop' | 'mobile' | 'code'

  return (
    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18 }}>
      {/* LEFT: Form */}
      <Card style={{ padding:18 }}>
        <div style={{ fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14 }}>📝 Email Content</div>

        <Btn v="primary" icon="✨" full onClick={() => setAiOpen(true)} style={{ marginBottom:16 }}>
          Generate with AI (recommended)
        </Btn>

        <div style={{ marginBottom:14 }}>
          <Label>Subject Line *</Label>
          <input
            type="text"
            value={draft.subject || ""}
            onChange={e => setDraft({ ...draft, subject: e.target.value })}
            placeholder="e.g. Your VIP glow awaits, {{first_name}} ✨"
            style={inp}
            maxLength={150}
          />
          <Hint>{(draft.subject || "").length}/150 chars · Use {`{{first_name}}`} for personalization</Hint>
        </div>

        <div style={{ marginBottom:14 }}>
          <Label>Preheader</Label>
          <input
            type="text"
            value={draft.preheader || ""}
            onChange={e => setDraft({ ...draft, preheader: e.target.value })}
            placeholder="Preview text shown next to subject in inbox"
            style={inp}
            maxLength={120}
          />
          <Hint>{(draft.preheader || "").length}/120 chars · Shows in Gmail/Outlook preview</Hint>
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14 }}>
          <div>
            <Label>From Name</Label>
            <input type="text" value={draft.from_name || ""} onChange={e => setDraft({ ...draft, from_name: e.target.value })} style={inp} />
          </div>
          <div>
            <Label>From Email</Label>
            <input type="email" value={draft.from_email || ""} onChange={e => setDraft({ ...draft, from_email: e.target.value })} style={inp} />
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <Label>Insert image into body</Label>
          <ImageUploader currentUrl="" onUploaded={(url) => {
            const imgTag = `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0 0 24px"><tr><td align="center"><img src="${url}" alt="Nova Med Spa" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;text-decoration:none;border-radius:8px"/></td></tr></table>`;
            setDraft({ ...draft, html_body: (draft.html_body || "") + "\n" + imgTag });
          }} />
          <Hint>Upload an image to insert at the end of your email body. You can rearrange the HTML manually below.</Hint>
        </div>

        <div>
          <Label>HTML Body *</Label>
          <textarea
            value={draft.html_body || ""}
            onChange={e => setDraft({ ...draft, html_body: e.target.value })}
            placeholder='<h1>Hi {{first_name}}!</h1><p>Your message here...</p>'
            rows={10}
            style={{ ...inp, resize:"vertical", fontFamily:"monospace", fontSize:11 }}
          />
          <Hint>HTML allowed · {`{{first_name}}`} and {`{{name}}`} are auto-replaced · Footer with unsubscribe added automatically</Hint>
        </div>
      </Card>

      {/* RIGHT: Preview */}
      <Card style={{ padding:0,overflow:"hidden",position:"sticky",top:0,maxHeight:"85vh",display:"flex",flexDirection:"column" }}>
        <div style={{ padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:C.goldBg,flexShrink:0 }}>
          <div style={{ fontSize:13,fontWeight:700,color:C.gold,fontFamily:S }}>👁 Live Preview</div>
          <div style={{ display:"flex",gap:4,background:C.surface,padding:3,borderRadius:7,border:`1px solid ${C.border}` }}>
            {[["edit","✏️ Edit"],["desktop","🖥"],["mobile","📱"],["code","</>"]].map(([m, ic]) => (
              <button key={m} onClick={() => setPreviewMode(m)} style={{ padding:"4px 8px",fontSize:11,fontWeight:previewMode===m?700:500,color:previewMode===m?"#fff":C.muted,background:previewMode===m?C.gold:"transparent",borderRadius:5,border:"none",cursor:"pointer" }}>{ic}</button>
            ))}
          </div>
        </div>

        <div style={{ flex:1,overflow:"auto",background:"#e5e7eb",padding:previewMode==="code"?0:16 }}>
          {previewMode === "code" ? (
            <pre style={{ fontFamily:"monospace",fontSize:11,padding:14,whiteSpace:"pre-wrap",margin:0,background:"#0f0f1e",color:"#a3e635",height:"100%",overflow:"auto" }}>{draft.html_body || "(empty)"}</pre>
          ) : previewMode === "edit" ? (
            <div>
              <div style={{ maxWidth:600,margin:"0 auto 8px",padding:"8px 14px",background:"#fff",borderRadius:6,fontSize:11,color:C.muted,border:`1px solid ${C.border}` }}>
                ✏️ Click any text to edit it directly. Select words for formatting toolbar. <strong style={{color:C.gold}}>{`{{first_name}}`}</strong> will be replaced with each recipient's real name.
              </div>
              <div style={{ maxWidth:600,margin:"0 auto",background:"#fff",borderRadius:6,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.1)" }}>
                <div style={{ padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,color:C.muted,background:C.light }}>
                  <div><strong style={{ color:C.text }}>{draft.from_name || "Nova Med Spa"}</strong> &lt;{draft.from_email || "admin@..."}&gt;</div>
                  <div style={{ fontWeight:700,color:C.text,marginTop:4,fontSize:13 }}>{draft.subject || "(no subject)"}</div>
                  {draft.preheader && <div style={{ fontSize:11,color:C.muted,marginTop:2 }}>{draft.preheader}</div>}
                </div>
                <div style={{ overflow:"hidden",maxWidth:"100%" }}>
                  <EmailWysiwyg html={draft.html_body} onChange={(newHtml) => setDraft({ ...draft, html_body: newHtml })} mode="desktop" />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ maxWidth:previewMode==="mobile"?380:600,margin:"0 auto",background:"#fff",borderRadius:6,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.1)" }}>
              <div style={{ padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,color:C.muted,background:C.light }}>
                <div><strong style={{ color:C.text }}>{draft.from_name || "Nova Med Spa"}</strong> &lt;{draft.from_email || "admin@..."}&gt;</div>
                <div style={{ fontWeight:700,color:C.text,marginTop:4,fontSize:13 }}>{draft.subject || "(no subject)"}</div>
                {draft.preheader && <div style={{ fontSize:11,color:C.muted,marginTop:2 }}>{draft.preheader}</div>}
              </div>
              <div style={{ overflow:"hidden",maxWidth:"100%" }} dangerouslySetInnerHTML={{ __html: (draft.html_body || "<p style='padding:40px;text-align:center;color:#9ca3af'>(empty body)</p>").replaceAll("{{first_name}}","Sarah").replaceAll("{{name}}","Sarah Smith") }} />
            </div>
          )}
        </div>
      </Card>

      {aiOpen && <AIGenerateModal onClose={() => setAiOpen(false)} onGenerated={(result) => {
        setDraft({ ...draft, subject:result.subject, preheader:result.preheader, html_body:result.html_body });
        setAiOpen(false);
      }} />}
    </div>
  );
}

function Label({ children }) {
  return <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>{children}</label>;
}
function Hint({ children }) {
  return <div style={{ fontSize:10,color:C.dim,marginTop:4 }}>{children}</div>;
}

// ────────────────────────────────────────────────────────────────────────────
// AI GENERATE MODAL
// ────────────────────────────────────────────────────────────────────────────
function AIGenerateModal({ onClose, onGenerated }) {
  const [prompt, setPrompt] = useState("");
  const [businessInfo, setBusinessInfo] = useState("Nova Med Spa offers Botox, Fillers, HydraFacial, Laser treatments, GLP-1 weight loss. Located in Beverly Hills. Open Mon-Sat 9am-7pm.");
  const [imageUrl, setImageUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const examples = [
    "Mother's Day Botox special, 20% off injectables, ends May 12",
    "New HydraFacial Platinum treatment launch announcement",
    "Win-back email for clients who haven't visited in 6+ months",
    "VIP-only invitation to private Sculptra event next Saturday",
    "Black Friday all treatments 30% off, limited time",
  ];

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError("");
    try {
      // Direct fetch (bypasses supabase.invoke timeout limit)
      const SUPABASE_URL = "https://fuoejykgpnfphrvqjuub.supabase.co";
      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ1b2VqeWtncG5mcGhydnFqdXViIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY2MDUwOTcsImV4cCI6MjA5MjE4MTA5N30.-Iea55F7GszWyCny9Br94t6A9vpD8oCyQPfyw1gE8fw";
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90s
      let data;
      try {
        const r = await fetch(`${SUPABASE_URL}/functions/v1/email-generate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": ANON_KEY,
            "Authorization": `Bearer ${ANON_KEY}`,
          },
          body: JSON.stringify({ prompt, business_info: businessInfo, image_url: imageUrl || undefined }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        data = await r.json();
      } catch (fe) {
        clearTimeout(timeoutId);
        if (fe.name === "AbortError") throw new Error("Generation took too long (>90s). Try a shorter prompt.");
        throw fe;
      }
      if (!data?.ok) throw new Error(data?.error || "Generation failed");
      onGenerated(data);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:C.surface,borderRadius:16,maxWidth:580,width:"100%",maxHeight:"90vh",overflow:"auto",padding:0 }}>
        <div style={{ background:`linear-gradient(135deg,${C.gold},${C.goldD})`,padding:"20px 24px",color:"#fff" }}>
          <div style={{ fontSize:11,fontWeight:600,opacity:0.9,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4 }}>✨ AI Email Generator</div>
          <div style={{ fontSize:20,fontWeight:800,fontFamily:S }}>Tell me what you want to send</div>
          <div style={{ fontSize:12,opacity:0.85,marginTop:4 }}>Claude will write a luxury, brand-perfect email for Nova Med Spa</div>
        </div>

        <div style={{ padding:24 }}>
          <div style={{ marginBottom:16 }}>
            <Label>What's the email about? *</Label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={3}
              placeholder="e.g. Black Friday Botox special — 30% off, ending Sunday Nov 30"
              style={{ ...inp, resize:"vertical" }}
            />
          </div>

          <div style={{ marginBottom:16 }}>
            <Label>Quick ideas:</Label>
            <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
              {examples.map(ex => (
                <button key={ex} onClick={() => setPrompt(ex)} style={{ padding:"6px 11px",fontSize:11,background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:16,cursor:"pointer",fontFamily:F }}>{ex.split(",")[0]}</button>
              ))}
            </div>
          </div>

          <details style={{ marginBottom:16 }}>
            <summary style={{ fontSize:12,color:C.muted,cursor:"pointer",fontWeight:600 }}>Advanced options</summary>
            <div style={{ marginTop:12 }}>
              <Label>Business context</Label>
              <textarea
                value={businessInfo}
                onChange={e => setBusinessInfo(e.target.value)}
                rows={3}
                style={{ ...inp, resize:"vertical", fontSize:11 }}
              />
              <div style={{ marginTop:10 }}>
                <Label>Hero image (optional)</Label>
                <ImageUploader currentUrl={imageUrl} onUploaded={(url) => setImageUrl(url)} />
                <Hint>Upload a JPG/PNG up to 5MB — will be inserted at the top of the email</Hint>
              </div>
            </div>
          </details>

          {error && (
            <div style={{ padding:"10px 14px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:8,fontSize:12,marginBottom:14 }}>
              ❌ {error}
            </div>
          )}

          <div style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
            <Btn v="ghost" onClick={onClose} disabled={generating}>Cancel</Btn>
            <Btn v="primary" icon="✨" onClick={generate} disabled={generating || !prompt.trim()}>
              {generating ? "Generating..." : "Generate Email"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// STEP 2: AUDIENCE
// ────────────────────────────────────────────────────────────────────────────
function StepAudience({ draft, setDraft, campaignId, saveDraft }) {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [tags, setTags] = useState([]);
  const [memberPlans, setMemberPlans] = useState([]);
  const customEmails = draft.custom_emails_text || "";
  const setCustomEmails = (v) => setDraft({ ...draft, custom_emails_text: v });

  // Load distinct tags + member plans from clients
  useEffect(() => {
    const load = async () => {
      const { data: clients } = await supabase.from("clients").select("tags, member_plan");
      const allTags = new Set();
      const allPlans = new Set();
      (clients || []).forEach(c => {
        // tags can be array, JSON string, or null - normalize
        let tagList = c.tags;
        if (typeof tagList === "string") {
          try { tagList = JSON.parse(tagList); } catch { tagList = []; }
        }
        if (Array.isArray(tagList)) tagList.forEach(t => t && allTags.add(t));
        if (c.member_plan) allPlans.add(c.member_plan);
      });
      setTags([...allTags].sort());
      setMemberPlans([...allPlans].sort());
    };
    load();
  }, []);

  const previewAudience = async () => {
    const id = await saveDraft();
    if (!id) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const customList = customEmails.split(/[\n,;]/).map(e => e.trim()).filter(Boolean);
      const body = customList.length > 0
        ? { campaign_id: id, preview_only: true, custom_emails: customList }
        : { campaign_id: id, preview_only: true };
      const { data } = await supabase.functions.invoke("email-build-audience", { body });
      setPreview(data);
    } catch (e) {
      setPreview({ error: String(e?.message || e) });
    } finally {
      setPreviewing(false);
    }
  };

  const updateAudience = (audience_type, audience_filter = {}) => {
    setDraft({ ...draft, audience_type, audience_filter });
    setPreview(null);
  };

  return (
    <Card style={{ padding:20 }}>
      <div style={{ fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:16 }}>👥 Who gets this email?</div>

      <div style={{ fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>👤 Existing Clients</div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10,marginBottom:18 }}>
        <AudienceOption
          active={draft.audience_type === "all"}
          icon="🌍"
          label="All Active Clients"
          desc="Every client with an email"
          onClick={() => updateAudience("all")}
        />
        <AudienceOption
          active={draft.audience_type === "tag"}
          icon="🏷"
          label="By Tag"
          desc="VIP, Member, Botox, etc."
          onClick={() => updateAudience("tag", { tag: tags[0] || "VIP" })}
        />
        <AudienceOption
          active={draft.audience_type === "segment"}
          icon="🎯"
          label="Custom Segment"
          desc="By spend, plan, status"
          onClick={() => updateAudience("segment", {})}
        />
        <AudienceOption
          active={draft.audience_type === "custom"}
          icon="✏️"
          label="Custom Email List"
          desc="Type or paste emails"
          onClick={() => updateAudience("custom", {})}
        />
      </div>

      <div style={{ fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>🔥 Leads (FB/IG/Google ads)</div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10,marginBottom:18 }}>
        <AudienceOption
          active={draft.audience_type === "leads"}
          icon="🔥"
          label="All Warm Leads"
          desc="Leads not booked, not opted out"
          onClick={() => updateAudience("leads")}
        />
        <AudienceOption
          active={draft.audience_type === "leads_source"}
          icon="📊"
          label="Leads by Source"
          desc="Facebook / Instagram / Google"
          onClick={() => updateAudience("leads_source", { lead_source: "facebook" })}
        />
        <AudienceOption
          active={draft.audience_type === "leads_interest"}
          icon="💆"
          label="Leads by Interest"
          desc="Facial / Botox / Filler"
          onClick={() => updateAudience("leads_interest", { lead_interest: "facial" })}
        />
        <AudienceOption
          active={draft.audience_type === "combined"}
          icon="🎁"
          label="Clients + Leads"
          desc="Both, deduplicated"
          onClick={() => updateAudience("combined")}
        />
      </div>

      {/* Sub-options */}
      {draft.audience_type === "tag" && (
        <div style={{ padding:14,background:C.light,borderRadius:9,marginBottom:14 }}>
          <Label>Pick a tag:</Label>
          <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
            {tags.length === 0 && <span style={{ fontSize:12,color:C.muted }}>No tags yet — add tags to clients first</span>}
            {tags.map(t => (
              <button key={t} onClick={() => updateAudience("tag", { tag: t })} style={{ padding:"6px 12px",fontSize:11,fontWeight:draft.audience_filter?.tag===t?700:500,background:draft.audience_filter?.tag===t?C.gold:C.surface,color:draft.audience_filter?.tag===t?"#fff":C.text,border:`1px solid ${draft.audience_filter?.tag===t?C.gold:C.border}`,borderRadius:16,cursor:"pointer",fontFamily:F }}>{t}</button>
            ))}
          </div>
        </div>
      )}

      {draft.audience_type === "leads_source" && (
        <div style={{ padding:14,background:C.light,borderRadius:9,marginBottom:14 }}>
          <Label>Pick a lead source:</Label>
          <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
            {["facebook","instagram","google","tiktok","website","referral","other"].map(s => (
              <button key={s} onClick={() => updateAudience("leads_source", { lead_source: s })} style={{ padding:"6px 12px",fontSize:11,fontWeight:draft.audience_filter?.lead_source===s?700:500,background:draft.audience_filter?.lead_source===s?C.gold:C.surface,color:draft.audience_filter?.lead_source===s?"#fff":C.text,border:`1px solid ${draft.audience_filter?.lead_source===s?C.gold:C.border}`,borderRadius:16,cursor:"pointer",fontFamily:F,textTransform:"capitalize" }}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {draft.audience_type === "leads_interest" && (
        <div style={{ padding:14,background:C.light,borderRadius:9,marginBottom:14 }}>
          <Label>Pick an interest:</Label>
          <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
            {["facial","botox","filler","laser","weightloss","wellness","ivtherapy","consultation"].map(s => (
              <button key={s} onClick={() => updateAudience("leads_interest", { lead_interest: s })} style={{ padding:"6px 12px",fontSize:11,fontWeight:draft.audience_filter?.lead_interest===s?700:500,background:draft.audience_filter?.lead_interest===s?C.gold:C.surface,color:draft.audience_filter?.lead_interest===s?"#fff":C.text,border:`1px solid ${draft.audience_filter?.lead_interest===s?C.gold:C.border}`,borderRadius:16,cursor:"pointer",fontFamily:F,textTransform:"capitalize" }}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {draft.audience_type === "segment" && (
        <div style={{ padding:14,background:C.light,borderRadius:9,marginBottom:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
          <div>
            <Label>Min Spent ($)</Label>
            <input type="number" value={draft.audience_filter?.min_spent || ""} onChange={e => updateAudience("segment", { ...draft.audience_filter, min_spent: e.target.value ? +e.target.value : undefined })} placeholder="e.g. 500" style={inp} />
          </div>
          <div>
            <Label>Max Spent ($)</Label>
            <input type="number" value={draft.audience_filter?.max_spent || ""} onChange={e => updateAudience("segment", { ...draft.audience_filter, max_spent: e.target.value ? +e.target.value : undefined })} placeholder="optional" style={inp} />
          </div>
          <div>
            <Label>Member Plan</Label>
            <select value={draft.audience_filter?.member_plan || ""} onChange={e => updateAudience("segment", { ...draft.audience_filter, member_plan: e.target.value || undefined })} style={inp}>
              <option value="">Any</option>
              {memberPlans.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label>Status</Label>
            <select value={draft.audience_filter?.status || ""} onChange={e => updateAudience("segment", { ...draft.audience_filter, status: e.target.value || undefined })} style={inp}>
              <option value="">Any</option>
              <option value="active">Active</option>
              <option value="hold">On Hold</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      )}

      {draft.audience_type === "custom" && (
        <div style={{ padding:14,background:C.light,borderRadius:9,marginBottom:14 }}>
          <Label>Paste emails (one per line, comma, or semicolon)</Label>
          <textarea
            value={customEmails}
            onChange={e => setCustomEmails(e.target.value)}
            rows={5}
            placeholder="alice@example.com&#10;bob@example.com&#10;carol@example.com"
            style={{ ...inp, fontFamily:"monospace", fontSize:12 }}
          />
          <Hint>{customEmails.split(/[\n,;]/).filter(e => e.trim()).length} emails entered</Hint>
        </div>
      )}

      <Btn v="primary" icon="🔍" onClick={previewAudience} disabled={previewing}>
        {previewing ? "Calculating..." : "Preview Audience"}
      </Btn>

      {preview && (
        <div style={{ marginTop:16,padding:16,background:preview.error?C.redL:C.greenL,borderRadius:10,border:`1px solid ${preview.error?C.red:C.green}40` }}>
          {preview.error ? (
            <div style={{ color:C.red,fontSize:13 }}>❌ {preview.error}</div>
          ) : (
            <>
              <div style={{ display:"flex",alignItems:"center",gap:14,marginBottom:10 }}>
                <div style={{ fontSize:32,fontWeight:800,color:C.green,fontFamily:S }}>{preview.will_send?.toLocaleString() || preview.total?.toLocaleString()}</div>
                <div>
                  <div style={{ fontSize:13,fontWeight:700,color:C.text }}>recipients will receive this</div>
                  {preview.suppressed > 0 && <div style={{ fontSize:11,color:C.muted }}>{preview.suppressed} skipped (unsubscribed/bounced)</div>}
                </div>
              </div>
              {preview.sample?.length > 0 && (
                <div style={{ fontSize:11,color:C.muted,fontStyle:"italic" }}>
                  Sample: {preview.sample.map(s => s.name || s.email).join(", ")}{preview.total > 5 ? "..." : ""}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function AudienceOption({ active, icon, label, desc, onClick }) {
  return (
    <button onClick={onClick} style={{ padding:14,background:active?C.goldBg:C.surface,border:`2px solid ${active?C.gold:C.border}`,borderRadius:10,cursor:"pointer",textAlign:"left",fontFamily:F,transition:"all 0.15s" }}>
      <div style={{ fontSize:24,marginBottom:6 }}>{icon}</div>
      <div style={{ fontSize:13,fontWeight:700,color:C.text,marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:11,color:C.muted }}>{desc}</div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// STEP 3: REVIEW & SEND
// ────────────────────────────────────────────────────────────────────────────
function StepSend({ draft, setDraft, campaignId, saveDraft, onSent }) {
  const [audienceCount, setAudienceCount] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sendMode, setSendMode] = useState("now"); // 'now' | 'schedule'
  const [scheduleDate, setScheduleDate] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Build audience and poll DB for count
  useEffect(() => {
    let cancelled = false;
    let pollTimer;

    const build = async () => {
      const id = await saveDraft();
      if (!id || cancelled) return;
      const body = { campaign_id: id, preview_only: false };
      // For custom audience, pass the typed emails
      if (draft.audience_type === "custom" && draft.custom_emails_text) {
        body.custom_emails = draft.custom_emails_text.split(/[\n,;]/).map(e => e.trim()).filter(Boolean);
      }
      // Fire build-audience but don't wait (it can take 30s+ for thousands of leads)
      supabase.functions.invoke("email-build-audience", { body }).catch(() => {});

      // Poll recipient_count from DB until stable
      let lastCount = -1;
      let stableTicks = 0;
      const poll = async () => {
        if (cancelled) return;
        const { data } = await supabase.from("email_campaigns").select("recipient_count").eq("id", id).single();
        const cnt = data?.recipient_count || 0;
        setAudienceCount(cnt);
        if (cnt === lastCount && cnt > 0) {
          stableTicks++;
          if (stableTicks >= 2) return;
        } else {
          stableTicks = 0;
          lastCount = cnt;
        }
        pollTimer = setTimeout(poll, 1500);
      };
      poll();
    };
    build();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) return;
    const id = await saveDraft();
    if (!id) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("email-send", {
        body: { campaign_id: id, test_email: testEmail },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Test failed");
      setTestResult({ ok:true, message:"Test email sent! Check your inbox." });
    } catch (e) {
      setTestResult({ ok:false, message:String(e?.message || e) });
    } finally {
      setTestSending(false);
    }
  };

  const launch = async () => {
    if (!audienceCount) {
      window.alert("No recipients! Go back to step 2 and pick an audience.");
      return;
    }
    if (sendMode === "schedule" && !scheduleDate) {
      window.alert("Pick a schedule date/time");
      return;
    }
    if (!window.confirm(`Send to ${audienceCount} recipients now?`)) return;

    const id = await saveDraft();
    if (!id) return;

    setSending(true);
    setSendResult(null);

    try {
      if (sendMode === "schedule") {
        await supabase.from("email_campaigns").update({
          status: "scheduled",
          scheduled_for: new Date(scheduleDate).toISOString(),
        }).eq("id", id);
        setSendResult({ ok:true, message:`Scheduled for ${new Date(scheduleDate).toLocaleString()}` });
        setTimeout(onSent, 1500);
        return;
      }

      // Don't wait — fire and forget. Batched cron-driven send happens in background.
      supabase.functions.invoke("email-send", { body: { campaign_id: id } }).catch(() => {});
      // Update DB so list shows "sending" immediately
      await supabase.from("email_campaigns").update({ status: "sending", send_started_at: new Date().toISOString() }).eq("id", id);
      setSendResult({ ok:true, message:`Sending started! Watch progress in the Campaigns list.` });
      setTimeout(onSent, 2500);
    } catch (e) {
      setSendResult({ ok:false, message:String(e?.message || e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18 }}>
      {/* LEFT: Summary + Send */}
      <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
        <Card style={{ padding:18 }}>
          <div style={{ fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12 }}>📋 Campaign Summary</div>

          <SummaryRow label="From" value={`${draft.from_name} <${draft.from_email}>`} />
          <SummaryRow label="Subject" value={draft.subject || "—"} />
          {draft.preheader && <SummaryRow label="Preheader" value={draft.preheader} />}
          <SummaryRow label="Recipients" value={audienceCount === null ? "Calculating..." : `${audienceCount.toLocaleString()} recipients`} highlight />
        </Card>

        <Card style={{ padding:18 }}>
          <div style={{ fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10 }}>🧪 Send a Test First</div>
          <div style={{ fontSize:11,color:C.muted,marginBottom:10 }}>Send this email only to one address before launching to your full list.</div>
          <div style={{ display:"flex",gap:8 }}>
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="your@email.com" style={{ ...inp, flex:1 }} />
            <Btn v="sec" onClick={sendTest} disabled={testSending || !testEmail}>{testSending ? "..." : "Send Test"}</Btn>
          </div>
          {testResult && (
            <div style={{ marginTop:10,padding:"8px 12px",borderRadius:8,fontSize:12,background:testResult.ok?C.greenL:C.redL,color:testResult.ok?C.green:C.red }}>
              {testResult.ok ? "✓ " : "❌ "}{testResult.message}
            </div>
          )}
        </Card>

        <Card style={{ padding:18 }}>
          <div style={{ fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12 }}>🚀 Launch</div>

          <div style={{ display:"flex",gap:6,padding:3,background:C.light,borderRadius:8,marginBottom:12 }}>
            {[["now","Send Now"],["schedule","Schedule"]].map(([m, l]) => (
              <button key={m} onClick={() => setSendMode(m)} style={{ flex:1,padding:"8px 10px",fontSize:12,fontWeight:sendMode===m?700:500,color:sendMode===m?"#fff":C.muted,background:sendMode===m?C.gold:"transparent",borderRadius:6,border:"none",cursor:"pointer",fontFamily:F }}>{l}</button>
            ))}
          </div>

          {sendMode === "schedule" && (
            <div style={{ marginBottom:12 }}>
              <Label>Send at:</Label>
              <input type="datetime-local" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} style={inp} />
              <Hint>Local time. Cron job sends within 5 minutes of this time.</Hint>
            </div>
          )}

          <Btn v={sendMode==="now"?"success":"primary"} sz="lg" full disabled={sending || !audienceCount} onClick={launch}>
            {sending ? "Launching..." : sendMode === "now" ? `🚀 Send to ${audienceCount?.toLocaleString() || "..."} now` : "📅 Schedule"}
          </Btn>

          {sendResult && (
            <div style={{ marginTop:14,padding:"12px 14px",borderRadius:9,fontSize:13,fontWeight:600,background:sendResult.ok?C.greenL:C.redL,color:sendResult.ok?C.green:C.red }}>
              {sendResult.ok ? "✓ " : "❌ "}{sendResult.message}
            </div>
          )}
        </Card>
      </div>

      {/* RIGHT: Final preview */}
      <Card style={{ padding:0,overflow:"hidden",position:"sticky",top:0,maxHeight:"85vh",display:"flex",flexDirection:"column" }}>
        <div style={{ padding:"12px 16px",borderBottom:`1px solid ${C.border}`,background:C.goldBg,fontSize:13,fontWeight:700,color:C.gold,fontFamily:S }}>👁 Final Preview</div>
        <div style={{ flex:1,overflow:"auto",background:"#e5e7eb",padding:16 }}>
          <div style={{ maxWidth:600,margin:"0 auto",background:"#fff",borderRadius:6,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.1)" }}>
            <div style={{ padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,color:C.muted,background:C.light }}>
              <div><strong style={{ color:C.text }}>{draft.from_name}</strong> &lt;{draft.from_email}&gt;</div>
              <div style={{ fontWeight:700,color:C.text,marginTop:4,fontSize:13 }}>{(draft.subject || "").replaceAll("{{first_name}}","Sarah")}</div>
              {draft.preheader && <div style={{ fontSize:11,color:C.muted,marginTop:2 }}>{draft.preheader}</div>}
            </div>
            <div style={{ overflow:"hidden",maxWidth:"100%" }} dangerouslySetInnerHTML={{ __html: (draft.html_body || "<p style='padding:40px;text-align:center;color:#9ca3af'>(empty)</p>").replaceAll("{{first_name}}","Sarah").replaceAll("{{name}}","Sarah Smith") }} />
          </div>
        </div>
      </Card>
    </div>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <div style={{ display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13 }}>
      <span style={{ color:C.muted }}>{label}</span>
      <span style={{ color:highlight?C.gold:C.text,fontWeight:highlight?700:500,textAlign:"right",maxWidth:"60%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{value}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ANALYTICS DETAIL VIEW
// ────────────────────────────────────────────────────────────────────────────
function AnalyticsDetail({ campaign, onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | 'opened' | 'clicked' | 'bounced'

  const load = async () => {
    setLoading(true);
    const { data: rec } = await supabase.from("email_recipients")
      .select("*").eq("campaign_id", campaign.id).order("id");
    setRecipients(rec || []);
    const { data: ev } = await supabase.from("email_events")
      .select("*").eq("campaign_id", campaign.id).order("created_at", { ascending: false }).limit(100);
    setEvents(ev || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("analytics-realtime-" + campaign.id)
      .on("postgres_changes", { event:"*", schema:"public", table:"email_recipients", filter:`campaign_id=eq.${campaign.id}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);

  const totals = useMemo(() => {
    const total = recipients.length;
    const sent = recipients.filter(r => r.sent_at).length;
    const delivered = recipients.filter(r => r.delivered_at).length;
    const opened = recipients.filter(r => r.opened_at).length;
    const clicked = recipients.filter(r => r.first_clicked_at).length;
    const bounced = recipients.filter(r => r.status === "bounced").length;
    const unsub = recipients.filter(r => r.status === "unsubscribed").length;
    return { total, sent, delivered, opened, clicked, bounced, unsub };
  }, [recipients]);

  const openRate = totals.delivered > 0 ? (totals.opened / totals.delivered * 100).toFixed(1) : "0.0";
  const clickRate = totals.delivered > 0 ? (totals.clicked / totals.delivered * 100).toFixed(1) : "0.0";
  const ctr = totals.opened > 0 ? (totals.clicked / totals.opened * 100).toFixed(1) : "0.0";

  const filtered = recipients.filter(r => {
    if (filter === "all") return true;
    if (filter === "opened") return r.opened_at;
    if (filter === "clicked") return r.first_clicked_at;
    if (filter === "bounced") return r.status === "bounced";
    return true;
  });

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:18 }}>
      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap" }}>
        <div style={{ display:"flex",alignItems:"center",gap:12 }}>
          <Btn v="ghost" sz="sm" onClick={onClose}>← Back</Btn>
          <div>
            <div style={{ fontSize:18,fontWeight:800,color:C.text,fontFamily:S }}>{campaign.name}</div>
            <div style={{ fontSize:11,color:C.muted }}>{campaign.subject}</div>
          </div>
        </div>
        <Bdg color={STATUS_BADGE[campaign.status]?.color || C.muted}>{STATUS_BADGE[campaign.status]?.label}</Bdg>
      </div>

      {/* Big stats */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12 }}>
        <BigStat label="Sent" value={totals.sent.toLocaleString()} sub={`of ${totals.total}`} color={C.gold} />
        <BigStat label="Delivered" value={totals.delivered.toLocaleString()} sub={`${totals.sent>0?(totals.delivered/totals.sent*100).toFixed(1):0}%`} color={C.green} />
        <BigStat label="Open Rate" value={`${openRate}%`} sub={`${totals.opened.toLocaleString()} opens`} color={C.blue} />
        <BigStat label="Click Rate" value={`${clickRate}%`} sub={`${totals.clicked.toLocaleString()} clicks`} color={C.purple} />
        <BigStat label="CTR (clicks/opens)" value={`${ctr}%`} color={C.teal} />
        {totals.bounced > 0 && <BigStat label="Bounced" value={totals.bounced.toLocaleString()} color={C.red} />}
        {totals.unsub > 0 && <BigStat label="Unsubscribed" value={totals.unsub.toLocaleString()} color={C.orange} />}
      </div>

      {/* Recipients table */}
      <Card>
        <div style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,background:C.goldBg }}>
          <div style={{ fontSize:14,fontWeight:700,color:C.gold,fontFamily:S }}>Recipients ({filtered.length})</div>
          <div style={{ display:"flex",gap:4 }}>
            {[["all","All"],["opened","Opened"],["clicked","Clicked"],["bounced","Bounced"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{ padding:"5px 11px",fontSize:11,fontWeight:filter===k?700:500,color:filter===k?"#fff":C.muted,background:filter===k?C.gold:"transparent",border:`1px solid ${filter===k?C.gold:C.border}`,borderRadius:6,cursor:"pointer",fontFamily:F }}>{l}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ padding:30,textAlign:"center",color:C.muted,fontSize:13 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:30,textAlign:"center",color:C.muted,fontSize:13 }}>No recipients match this filter</div>
        ) : (
          <div style={{ maxHeight:480,overflow:"auto" }}>
            {filtered.slice(0, 200).map(r => (
              <div key={r.id} style={{ padding:"10px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:12,fontSize:12 }}>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{r.name || r.email}</div>
                  <div style={{ fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{r.email}</div>
                </div>
                <div style={{ display:"flex",gap:4 }}>
                  {r.sent_at && <Bdg color={C.gold}>Sent</Bdg>}
                  {r.delivered_at && <Bdg color={C.green}>Delivered</Bdg>}
                  {r.opened_at && <Bdg color={C.blue}>Opened {r.open_count}×</Bdg>}
                  {r.first_clicked_at && <Bdg color={C.purple}>Clicked {r.click_count}×</Bdg>}
                  {r.status === "bounced" && <Bdg color={C.red}>Bounced</Bdg>}
                  {r.status === "unsubscribed" && <Bdg color={C.orange}>Unsubscribed</Bdg>}
                  {r.status === "failed" && <Bdg color={C.red}>Failed</Bdg>}
                </div>
              </div>
            ))}
            {filtered.length > 200 && (
              <div style={{ padding:14,textAlign:"center",fontSize:11,color:C.dim }}>Showing first 200 of {filtered.length}</div>
            )}
          </div>
        )}
      </Card>

      {/* Recent events */}
      {events.length > 0 && (
        <Card>
          <div style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}`,fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,background:C.goldBg }}>
            Recent Activity (last 100 events)
          </div>
          <div style={{ maxHeight:300,overflow:"auto" }}>
            {events.map(e => (
              <div key={e.id} style={{ padding:"8px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10,fontSize:12 }}>
                <div style={{ width:80 }}>
                  <Bdg color={e.event_type==="opened"?C.blue:e.event_type==="clicked"?C.purple:e.event_type==="delivered"?C.green:e.event_type==="bounced"?C.red:C.muted}>{e.event_type}</Bdg>
                </div>
                <div style={{ flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:C.muted }}>{e.email}</div>
                <div style={{ fontSize:11,color:C.dim }}>{new Date(e.created_at).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function BigStat({ label, value, sub, color }) {
  return (
    <Card style={{ padding:"16px 18px",borderTop:`3px solid ${color}` }}>
      <div style={{ fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:28,fontWeight:800,color,fontFamily:S }}>{value}</div>
      {sub && <div style={{ fontSize:11,color:C.dim,marginTop:2 }}>{sub}</div>}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SUPPRESSIONS
// ────────────────────────────────────────────────────────────────────────────
function Suppressions({ onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("email_suppressions").select("*").order("created_at", { ascending: false });
    setList(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from suppression list? They will receive future emails.`)) return;
    await supabase.from("email_suppressions").delete().eq("email", email);
    load();
  };

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:18 }}>
      <div style={{ display:"flex",alignItems:"center",gap:12 }}>
        <Btn v="ghost" sz="sm" onClick={onClose}>← Back</Btn>
        <div>
          <div style={{ fontSize:18,fontWeight:800,color:C.text,fontFamily:S }}>Suppression List</div>
          <div style={{ fontSize:11,color:C.muted }}>People who unsubscribed, bounced, or complained — they will never receive emails from you.</div>
        </div>
      </div>

      <Card>
        <div style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:C.goldBg,fontSize:14,fontWeight:700,color:C.gold,fontFamily:S }}>
          {list.length.toLocaleString()} suppressed
        </div>
        {loading ? (
          <div style={{ padding:30,textAlign:"center",color:C.muted,fontSize:13 }}>Loading…</div>
        ) : list.length === 0 ? (
          <div style={{ padding:40,textAlign:"center",color:C.muted,fontSize:13 }}>✨ No suppressed emails. Great deliverability!</div>
        ) : list.map(s => (
          <div key={s.id} style={{ padding:"12px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13,fontWeight:600,color:C.text }}>{s.email}</div>
              <div style={{ fontSize:11,color:C.muted }}>
                <Bdg color={s.reason==="bounced"?C.red:s.reason==="complained"?C.orange:C.muted}>{s.reason}</Bdg>
                <span style={{ marginLeft:8 }}>{new Date(s.created_at).toLocaleDateString("en-US",{ month:"short", day:"numeric", year:"numeric" })}</span>
              </div>
            </div>
            <Btn sz="sm" v="ghost" onClick={() => remove(s.email)}>Remove</Btn>
          </div>
        ))}
      </Card>
    </div>
  );
}
